<script setup lang="ts">
  import { post } from '~/services/donationScreen';
  import AsteriskText from '../Forms/AsteriskText.vue';
  import type { ResponseStatus } from '~/types';

  const props = defineProps<{
    visible: boolean;
    close: () => void;
    budgetId: string;
    budgetAgainstExpensesNotApproved: boolean;
  }>();

  const modalState = reactive<{
    edit_reason: string;
    password: string;
  }>({
    edit_reason: '',
    password: ''
  });

  const loading = ref<ResponseStatus>('idle');
  const router = useRouter();

  const updateModalRef = ref();
  const updateModalRules = {
    edit_reason: [{ required: true, message: 'Reason is required!', trigger: 'submit' }],
    password: [{ required: true, message: 'Password is required!', trigger: 'submit' }]
  };

  const handleModalOk = async () => {
    loading.value = 'pending';
    try {
      const isValid = await updateModalRef.value.validate();
      if (isValid) {
        const response: any = await post('/v1/close_budget', {
          data: {
            uid: props.budgetId,
            edit_reason: modalState.edit_reason,
            edit_password: modalState.password
          }
        });
        if (response.data.status === 'success') {
          loading.value = 'success';
          openSuccessfulCustomNotification(response.data.message);
          router.push('/budgets');
        } else if (response.data.status === 'error') {
          loading.value = 'error';
          openFailedAdditionNotification(response.data.message);
        }
      }
    } catch (error: any) {
      loading.value = 'error';
      if (error?.data?.message) {
        openFailedAdditionNotification(error.data.message);
      }
      console.error(error);
    } finally {
      if (loading.value === 'pending') loading.value = 'idle';
    }
  };
</script>

<template>
  <a-modal
    :visible="props.visible"
    title="Close Budget"
    class="account-info-modal"
    ok-text="Deny"
    @ok="handleModalOk"
    @cancel="props.close"
  >
    <a-form
      ref="updateModalRef"
      layout="vertical"
      label-align="left"
      class="flex flex-col gap-3"
      :label-col="{ style: { width: '183px' } }"
      :colon="false"
      :rules="updateModalRules"
      :model="modalState"
      :hide-required-mark="true"
    >
      <p
        v-if="budgetAgainstExpensesNotApproved"
        class="text-sm py-2 px-3 rounded-[4px] font-semibold bg-yellow-100 border border-orange-400"
      >
        ⚠️ This budget has unapproved expenses. Are you sure you want to close it? You can review them before
        proceeding.
      </p>
      <a-form-item name="edit_reason" class="!sm-medium">
        <template #label>
          <AsteriskText label="Reason for close budget" />
        </template>
        <a-textarea v-model:value="modalState.edit_reason" class="w-[547px]" :rows="3" />
      </a-form-item>
      <a-form-item name="password" class="!sm-medium">
        <template #label>
          <AsteriskText label="Edit Password" />
        </template>
        <a-input v-model:value="modalState.password" type="password" class="w-[248px]" />
      </a-form-item>
    </a-form>
    <template #footer>
      <div class="flex gap-1 w-full justify-end border-t border-gray-300 p-3 bg-gray-50 rounded-b-lg">
        <a-button
          :disabled="loading === 'pending'"
          class="rounded text-sm font-medium border border-gray-300 text-gray-500 px-3 py-1.5"
          @click="props.close"
          >Cancel</a-button
        >
        <a-button
          :disabled="loading === 'pending'"
          class="px-3 border !border-red-700 bg-red-700 !text-white disabled:hover:!bg-red-50 hover:!bg-red-600 disabled:!text-red-300 disabled:!border-red-300 disabled:bg-red-50 rounded py-1.5 flex items-center justify-center text-sm font-medium"
          type="primary"
          @click="handleModalOk"
          >Close Budget</a-button
        >
      </div>
    </template>
  </a-modal>
</template>

<style scoped>
  @import '~/assets/css/modal.css';

  .ant-form-item {
    margin-bottom: 0px !important;
  }
</style>
